import React, { useState, useEffect } from 'react';
import { IForm } from './EmbedClaimPortal';
import CodeMirror from '@uiw/react-codemirror';
import { html } from '@codemirror/lang-html';
import { generateUniqueId, NotificationTypes } from 'utils';
import { Modal } from 'components/Modal';

interface ICodeScriptProps {
  form: IForm;
  showNotification: (type: NotificationTypes, message: string) => void;
}
const CodeScript = ({ form, showNotification }: ICodeScriptProps) => {
  const [injectableScriptString] = useState(
    `<script src="${process.env.REACT_APP_CLAIM_PORTAL_EMBEDSCRIPT_URL}" async></script>`,
  );
  const [script, setScript] = useState('');
  const [showScreenshotModal, setShowScreenshotModal] = useState(false);

  const [step, setStep] = useState<number>(1);

  const onSetStep = (step: number) => {
    setStep(step);
  };

  useEffect(() => {
    const containerId = generateUniqueId();
    // The copied JavaScript is embedded in HTML, whose parser also recognizes </script> inside strings.
    const keyLiteral = JSON.stringify(typeof form.key === 'string' ? form.key : '')
      .replace(/</g, '\\u003c')
      .replace(/\u2028/g, '\\u2028')
      .replace(/\u2029/g, '\\u2029');
    const script = `
      <div style="width: 100%; height: 100vh" id="${containerId}"></div>
      <script type="text/javascript">
        function loadShipAidPortal() {
          let startTime = performance.now();
          const timeout = 10000;
  
          function checkLoaded() {
            if (typeof window.initShipAidPortal === 'function') {
              window.initShipAidPortal('#${containerId}', {
                key: ${keyLiteral}
              });
            } else if (performance.now() - startTime < timeout) {
              requestAnimationFrame(checkLoaded);
            } else {
              console.error("ShipAid Portal script failed to load within timeout.");
            }
          }
  
          requestAnimationFrame(checkLoaded);
        }
        loadShipAidPortal();
      </script>`;

    // Append script dynamically if needed
    setScript(script);
  }, [form]);

  const onCopy = (content: string) => {
    navigator.clipboard.writeText(content);
    showNotification(NotificationTypes.success, 'Copied successfully');
  };

  return (
    <div className="w-full pb-6">
      <div className="mb-6 pb-5 border-b border-[#e1e3e5]">
        <div className="flex items-center gap-2 mb-2">
          <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-[#2563EB] text-white text-xs font-semibold">
            3
          </span>
          <h1 className="text-[28px] font-semibold text-[#202223] leading-tight">
            Generate Code / Script
          </h1>
        </div>
        <p className="text-[#6d7175] text-sm max-w-2xl leading-relaxed">
          Get your integration code to embed the portal into your Shopify store.
          Follow the steps below to complete the setup.
        </p>
      </div>

      <div className="mb-6">
        <div className="flex items-center justify-between max-w-2xl">
          <div
            className="relative flex-1 cursor-pointer"
            onClick={onSetStep.bind(null, 1)}
          >
            <div className="flex flex-col items-center">
              <div
                className={`flex items-center justify-center w-10 h-10 rounded-full text-white font-semibold text-sm z-10 transition-colors ${
                  step >= 1 ? 'bg-[#2563EB]' : 'bg-[#c9cccf]'
                }`}
              >
                1
              </div>
              <p
                className={`mt-2 text-sm font-medium ${
                  step >= 1 ? 'text-[#202223]' : 'text-[#6d7175]'
                }`}
              >
                Add Script to Theme
              </p>
            </div>
            {step > 1 && (
              <div className="absolute top-5 left-1/2 w-full h-0.5 bg-[#2563EB]"></div>
            )}
          </div>

          <div
            className="relative flex-1 cursor-pointer"
            onClick={onSetStep.bind(null, 2)}
          >
            <div className="flex flex-col items-center">
              <div
                className={`flex items-center justify-center w-10 h-10 rounded-full text-white font-semibold text-sm z-10 transition-colors ${
                  step >= 2 ? 'bg-[#2563EB]' : 'bg-[#c9cccf]'
                }`}
              >
                2
              </div>
              <p
                className={`mt-2 text-sm font-medium ${
                  step >= 2 ? 'text-[#202223]' : 'text-[#6d7175]'
                }`}
              >
                Copy Integration Code
              </p>
            </div>
          </div>
        </div>
      </div>

      {step === 1 && (
        <div>
          <div className="bg-white rounded-lg p-6 border border-[#e1e3e5]">
            <h2 className="text-base font-semibold text-[#202223] mb-4">
              Step 1: Add Script to Your Shopify Theme
            </h2>

            <div className="bg-[#fafbfb] rounded-lg p-5 border border-[#e1e3e5] mb-5">
              <h4 className="text-sm font-semibold text-[#202223] mb-2">
                Option 1: Assisted Installation
              </h4>
              <p role="status" className="text-sm text-[#6d7175] mb-4 leading-relaxed">
                Automatic Claims installation is unavailable. Contact ShipAid
                support to confirm a supported Claims setup for your store.
              </p>
              <p className="text-sm text-[#6d7175] mb-4 leading-relaxed">
                Existing portal integrations remain unchanged. A previous browser
                confirmation does not verify installation on your current theme.
              </p>
              <a href="https://help.shipaid.com" target="_blank" rel="noopener noreferrer"
                className="text-sm font-medium text-blue-700 underline">
                Get Claims installation help
              </a>
            </div>

            <div className="bg-[#fafbfb] rounded-lg p-5 border border-[#e1e3e5]">
              <h4 className="text-sm font-semibold text-[#202223] mb-2">
                Option 2: Manual Installation (Advanced)
              </h4>
              <p className="text-sm text-[#6d7175] mb-4 leading-relaxed">
                If you prefer to add the script manually, navigate to your
                Shopify admin:{' '}
                <strong className="text-[#202223]">
                  Online Store → Themes → Actions → Edit code
                </strong>
                . Find the{' '}
                <code className="bg-white px-1.5 py-0.5 rounded border border-[#e1e3e5] text-xs">
                  theme.liquid
                </code>{' '}
                file and add the following script just before the closing{' '}
                <code className="bg-white px-1.5 py-0.5 rounded border border-[#e1e3e5] text-xs">
                  &lt;/head&gt;
                </code>{' '}
                tag:
              </p>

              <div className="relative mt-4">
                <div className="bg-[#202223] text-[#f6f6f7] rounded-md p-4 font-mono text-sm overflow-x-auto">
                  {injectableScriptString}
                </div>
                <button
                  onClick={onCopy.bind(null, injectableScriptString)}
                  className="absolute top-3 right-3 bg-[#6d7175] hover:bg-[#5c6063] text-xs text-white py-1.5 px-3 rounded transition-colors"
                >
                  Copy
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {step === 2 && (
        <div className="flex-1">
          <div className="bg-white rounded-lg p-6 border border-[#e1e3e5]">
            <div>
              <h2 className="text-base font-semibold text-[#202223] mb-4">
                Step 2: Copy Integration Code
              </h2>

              <div className="bg-[#fafbfb] rounded-lg p-5 border border-[#e1e3e5] mb-5">
                <div className="flex justify-between items-start gap-4">
                  <div className="flex-1">
                    <h4 className="text-sm font-semibold text-[#202223] mb-2">
                      Portal Integration Code
                    </h4>
                    <p className="text-sm text-[#6d7175] mb-3 leading-relaxed">
                      Copy the code below and paste it into your Shopify page
                      where you want the portal to appear.
                    </p>
                    <div className="bg-white rounded-md p-3 border border-[#e1e3e5] mb-3">
                      <p className="text-xs text-[#202223] font-medium mb-1">
                        How to add:
                      </p>
                      <ol className="text-xs text-[#6d7175] space-y-1 list-decimal list-inside">
                        <li>
                          Go to{' '}
                          <strong className="text-[#202223]">
                            Online Store → Pages
                          </strong>{' '}
                          in Shopify admin
                        </li>
                        <li>
                          Create a new page or edit an existing one (e.g.,
                          &quot;Claims Portal&quot;)
                        </li>
                        <li>
                          Click{' '}
                          <strong className="text-[#202223]">Show HTML</strong>{' '}
                          in the page editor
                        </li>
                        <li>Paste the code below into the HTML editor</li>
                        <li>Save and publish your page</li>
                      </ol>
                    </div>
                    <div className="text-sm">
                      <button
                        onClick={() => setShowScreenshotModal(true)}
                        className="text-[#2563EB] hover:text-[#1d4ed8] underline text-left"
                      >
                        View example screenshot →
                      </button>
                    </div>
                  </div>
                  <div>
                    <button
                      onClick={onCopy.bind(null, script)}
                      className="bg-[#2563EB] hover:bg-[#1d4ed8] text-sm text-white py-2.5 px-4 rounded-md font-medium transition-colors whitespace-nowrap"
                    >
                      Copy Code
                    </button>
                  </div>
                </div>
              </div>

              <div className="relative">
                <div className="bg-[#202223] text-[#f6f6f7] rounded-md p-4 font-mono text-sm overflow-x-auto">
                  <CodeMirror
                    className="cursor-pointer"
                    onClick={onCopy.bind(null, script)}
                    value={script}
                    editable={false}
                    extensions={[html()]}
                    theme="dark"
                  />
                </div>
                <button
                  onClick={onCopy.bind(null, script)}
                  className="absolute top-3 right-3 bg-[#6d7175] hover:bg-[#5c6063] text-xs text-white py-1.5 px-3 rounded transition-colors"
                >
                  Copy
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="flex items-center justify-between mt-6 pt-5 border-t border-[#e1e3e5]">
        <button
          disabled={step === 1}
          onClick={onSetStep.bind(null, 1)}
          className="px-4 py-2.5 text-sm font-medium text-[#202223] bg-white border border-[#c9cccf] hover:bg-[#f6f6f7] disabled:opacity-50 disabled:cursor-not-allowed rounded-md transition-colors"
        >
          ← Previous Step
        </button>
        <button
          disabled={step === 2}
          onClick={onSetStep.bind(null, 2)}
          className="px-4 py-2.5 text-sm font-medium text-white bg-[#2563EB] hover:bg-[#1d4ed8] disabled:opacity-50 disabled:cursor-not-allowed rounded-md transition-colors"
        >
          Next Step →
        </button>
      </div>

      {/* Screenshot Modal */}
      <Modal
        open={showScreenshotModal}
        openModal={setShowScreenshotModal}
        containerClass="sm:max-w-4xl sm:w-full"
      >
        <div className="p-6">
          <div className="mb-5">
            <h3 className="text-base font-semibold text-[#202223] mb-2">
              Example Screenshot
            </h3>
            <p className="text-sm text-[#6d7175]">
              This is an example of how to add the integration code in your
              Shopify page editor.
            </p>
          </div>

          <div className="border border-[#e1e3e5] rounded-lg overflow-hidden bg-white mb-4">
            <img
              src="http://res.cloudinary.com/media-resource/image/upload/v1740507980/shipaid-dev/t5exgw7jyrcxczpbwd1l.png"
              alt="Example screenshot showing how to add code in Shopify page editor"
              className="w-full h-auto"
            />
          </div>

          <div className="flex justify-end">
            <button
              onClick={() => setShowScreenshotModal(false)}
              className="px-4 py-2.5 text-sm font-medium text-white bg-[#2563EB] hover:bg-[#1d4ed8] rounded-md transition-colors"
            >
              Close
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default CodeScript;
